// src/pages/Reports.js
// Unified Reports Page - Terminal Theme
// Shows SCRUTINIZE scans, Fast Scans, VALIDATE jobs, and Trend reports

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { scanService } from '../services/api';
import { listReports, getReportViewUrl, getReportTypeLabel, getTrendData } from '../services/reportsService';
import { track as trackTelemetry } from '../services/telemetryService';
import CleanModernLayout from '../components/layouts/CleanModernLayout';
import ComplianceDashboard from '../components/ComplianceDashboard';
import RiskTrendChart from '../components/reports/RiskTrendChart';
import VulnTrendChart from '../components/reports/VulnTrendChart';
import DriftActivityChart from '../components/reports/DriftActivityChart';
import SeverityHeatcalendar from '../components/viz/SeverityHeatcalendar';
import './Reports.css';

// Filter dropdown options
const TYPE_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'scan', label: 'Scan' },
  { value: 'validate', label: 'VALIDATE' },
  { value: 'trend-monthly', label: 'Trend (Monthly)' },
  { value: 'trend-weekly', label: 'Trend (Weekly)' }
];

const RISK_OPTIONS = [
  { value: 'all', label: 'All Risk Levels' },
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' }
];

const STATUS_OPTIONS = [
  { value: 'all', label: 'All Statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'complete', label: 'Complete' },
  { value: 'failed', label: 'Failed' }
];

const SORTABLE_COLUMNS = [
  { key: 'createdAt', label: 'Date & Time' },
  { key: 'type', label: 'Type' },
  { key: 'target', label: 'Target' },
  { key: 'riskLevel', label: 'Risk Level' },
  { key: 'status', label: 'Status' }
];

function Reports() {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pdfLoading, setPdfLoading] = useState({}); // Track PDF generation per report
  const [activeView, setActiveView] = useState('reports'); // 'reports' | 'compliance' | 'trends'
  // Trends tab state
  const [trendData, setTrendData] = useState(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendAssets, setTrendAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState('all');

  // Load trend data when Trends tab is active
  useEffect(() => {
    if (activeView !== 'trends') return;
    const loadTrendData = async () => {
      setTrendLoading(true);
      try {
        const data = await getTrendData(selectedAsset === 'all' ? null : selectedAsset, 12);
        setTrendData(data);
        if (data.assets && data.assets.length > 0) {
          setTrendAssets(data.assets);
        }
      } catch (err) {
        console.error('Reports: Failed to load trend data:', err);
        setTrendData({ riskScores: [], vulnCounts: [], driftActivity: [] });
      } finally {
        setTrendLoading(false);
      }
    };
    loadTrendData();
  }, [activeView, selectedAsset]);

  // Filter states. ?type=trend-weekly is honored on mount so the Command Center
  // "Executive Report" button can deep-link straight to the user's weekly trend
  // reports (PRD-75 Stage 4 — exec-report-PDF button rewired 2026-05-24).
  const [searchParams] = useSearchParams();
  const initialFilterType = (() => {
    const v = searchParams.get('type');
    return TYPE_OPTIONS.some(o => o.value === v) ? v : 'all';
  })();
  const [filterType, setFilterType] = useState(initialFilterType);
  const [filterRisk, setFilterRisk] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterTarget, setFilterTarget] = useState('');

  // Sorting state
  const [sortColumn, setSortColumn] = useState('createdAt');
  const [sortDirection, setSortDirection] = useState('desc');

  const navigate = useNavigate();

  // Handle PDF download (for scans only)
  const handleDownloadPDF = async (e, report) => {
    e.stopPropagation();
    if (pdfLoading[report.id]) return;

    setPdfLoading(prev => ({ ...prev, [report.id]: true }));

    // PRD-151 WP2: report download telemetry (enums only, no report id/target).
    try { trackTelemetry('action', { meta: { feature: 'report', action: 'download' } }); } catch (err) { /* best-effort */ }

    try {
      console.log('Reports: Downloading PDF for:', report.id);
      // Pass force=true to bypass cache and regenerate with enhanced template
      await scanService.downloadPDF(report.id, report.scanType || 'scrutinize', true);
    } catch (err) {
      console.error('Reports: PDF download failed:', err);
      alert('Failed to generate PDF. Please try again.');
    } finally {
      setPdfLoading(prev => ({ ...prev, [report.id]: false }));
    }
  };

  const loadReports = useCallback(async () => {
    try {
      setLoading(true);
      console.log('Reports: Loading all reports');

      const response = await listReports({ limit: 100 });
      console.log('Reports: Received response:', response);

      setReports(response.reports || []);
      setError(null);
    } catch (err) {
      console.error('Reports: Failed to load reports:', err);
      setError(err.message);
      setReports([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleViewReport = (report) => {
    console.log('Reports: Opening report:', report.id, 'type:', report.reportType);
    // PRD-151 WP2: report view telemetry (enums only, no report id/target).
    try { trackTelemetry('action', { meta: { feature: 'report', action: 'view' } }); } catch (err) { /* best-effort */ }
    const url = getReportViewUrl(report);
    navigate(url);
  };

  const getRiskLevelColor = (level) => {
    const colors = {
      'critical': '#ff0000',
      'Critical': '#ff0000',
      'high': '#ff6b35',
      'High': '#ff6b35',
      'medium': '#F3BA24',
      'Medium': '#F3BA24',
      'low': '#4caf50',
      'Low': '#4caf50',
      'Unknown': '#888'
    };
    return colors[level] || '#888';
  };

  const getStatusStyle = (status) => {
    const normalizedStatus = status?.toLowerCase();
    const styles = {
      'pending': { background: 'rgba(243, 186, 36, 0.15)', color: 'var(--severity-medium, #F3BA24)', border: '1px solid var(--severity-medium, #F3BA24)' },
      'running': { background: 'rgba(0, 150, 255, 0.15)', color: 'var(--severity-info, #0096FF)', border: '1px solid var(--severity-info, #0096FF)' },
      'complete': { background: 'rgba(57, 255, 20, 0.15)', color: 'var(--severity-low, #39ff14)', border: '1px solid var(--severity-low, #39ff14)' },
      'failed': { background: 'rgba(255, 23, 68, 0.15)', color: 'var(--severity-critical, #ff1744)', border: '1px solid var(--severity-critical, #ff1744)' }
    };
    return styles[normalizedStatus] || styles['pending'];
  };

  const getReportTypeBadgeStyle = (report) => {
    const styles = {
      'scrutinize': { background: 'rgba(255, 0, 0, 0.12)', color: 'var(--severity-critical, #ff4444)', border: '1px solid var(--severity-critical, #ff4444)' },
      'fastscan': { background: 'rgba(0, 200, 255, 0.12)', color: 'var(--accent-primary, #00c8ff)', border: '1px solid var(--accent-primary, #00c8ff)' },
      'validate': { background: 'color-mix(in srgb, var(--accent-tertiary) 12%, transparent)', color: 'var(--accent-tertiary, #0096C7)', border: '1px solid var(--accent-tertiary, #0096C7)' },
      'trend': { background: 'rgba(46, 204, 113, 0.12)', color: 'var(--severity-low, #2ecc71)', border: '1px solid var(--severity-low, #2ecc71)' }
    };
    return styles[report.type] || styles[report.reportType] || styles['fastscan'];
  };

  // Helper functions for sorting
  const getRiskLevelOrder = (level) => {
    const order = { 'critical': 4, 'high': 3, 'medium': 2, 'low': 1 };
    return order[level?.toLowerCase()] || 0;
  };

  const getStatusOrder = (status) => {
    const order = { 'complete': 4, 'running': 3, 'pending': 2, 'failed': 1 };
    return order[status?.toLowerCase()] || 0;
  };

  const getNormalizedType = (report) => {
    if (report.reportType === 'trend') {
      return report.periodType === 'monthly' ? 'trend-monthly' : 'trend-weekly';
    }
    return report.reportType || report.type || 'scan';
  };

  // Handle column header click for sorting
  const handleSort = (columnKey) => {
    if (sortColumn === columnKey) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection(columnKey === 'createdAt' ? 'desc' : 'asc');
    }
  };

  // Get sort indicator class
  const getSortClass = (columnKey) => {
    if (sortColumn !== columnKey) return 'sortable';
    return `sortable ${sortDirection === 'asc' ? 'sort-asc' : 'sort-desc'}`;
  };

  // Clear all filters
  const handleClearFilters = () => {
    setFilterType('all');
    setFilterRisk('all');
    setFilterStatus('all');
    setFilterTarget('');
  };

  // Check if any filters are active
  const hasActiveFilters = filterType !== 'all' || filterRisk !== 'all' || filterStatus !== 'all' || filterTarget.trim() !== '';

  // Filter and sort reports
  const filteredAndSortedReports = useMemo(() => {
    let result = [...reports];

    // Apply type filter
    if (filterType !== 'all') {
      result = result.filter(report => {
        const normalizedType = getNormalizedType(report);
        return normalizedType === filterType;
      });
    }

    // Apply risk level filter
    if (filterRisk !== 'all') {
      result = result.filter(report =>
        report.riskLevel?.toLowerCase() === filterRisk.toLowerCase()
      );
    }

    // Apply status filter
    if (filterStatus !== 'all') {
      result = result.filter(report =>
        report.status?.toLowerCase() === filterStatus.toLowerCase()
      );
    }

    // Apply target text search filter
    if (filterTarget.trim()) {
      const searchTerm = filterTarget.toLowerCase().trim();
      result = result.filter(report =>
        report.target?.toLowerCase().includes(searchTerm)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'createdAt':
          comparison = new Date(a.createdAt) - new Date(b.createdAt);
          break;
        case 'type':
          comparison = getNormalizedType(a).localeCompare(getNormalizedType(b));
          break;
        case 'target':
          comparison = (a.target || '').localeCompare(b.target || '');
          break;
        case 'riskLevel':
          comparison = getRiskLevelOrder(a.riskLevel) - getRiskLevelOrder(b.riskLevel);
          break;
        case 'status':
          comparison = getStatusOrder(a.status) - getStatusOrder(b.status);
          break;
        default:
          comparison = 0;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });

    return result;
  }, [reports, filterType, filterRisk, filterStatus, filterTarget, sortColumn, sortDirection]);

  // Calculate stats
  const stats = {
    total: reports.length,
    complete: reports.filter(r => r.status === 'complete').length,
    highRisk: reports.filter(r =>
      r.riskLevel?.toLowerCase() === 'critical' || r.riskLevel?.toLowerCase() === 'high'
    ).length,
    scans: reports.filter(r => r.reportType === 'scan').length,
    validates: reports.filter(r => r.reportType === 'validate').length
  };

  if (loading) {
    return (
      <CleanModernLayout activePage="reports">
      <div className="reports-container">
        <div className="loading-state">
          <div className="loading-spinner"></div>
          <p className="loading-text">LOADING REPORTS...</p>
          <p className="loading-subtext">Retrieving report history</p>
        </div>
      </div>
      </CleanModernLayout>
    );
  }

  if (error) {
    return (
      <CleanModernLayout activePage="reports">
      <div className="reports-container">
        <div className="error-state">
          <h2 className="error-title">ERROR</h2>
          <p className="error-text">{error}</p>
          <button onClick={loadReports} className="nav-button">
            Retry
          </button>
        </div>
      </div>
      </CleanModernLayout>
    );
  }

  return (
    <CleanModernLayout activePage="reports">
    <div className="reports-container">

      {/* Stats Bar */}
      <div className="reports-stats">
        <div className="stat-item">
          <span className="stat-number">{stats.total}</span>
          <span className="stat-label">Total Reports</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.complete}</span>
          <span className="stat-label">Completed</span>
        </div>
        <div className="stat-item">
          <span className="stat-number">{stats.highRisk}</span>
          <span className="stat-label">High Risk</span>
        </div>
      </div>

      {/* View Tabs */}
      <div className="reports-view-tabs">
        <button
          className={`reports-view-tab ${activeView === 'reports' ? 'active' : ''}`}
          onClick={() => setActiveView('reports')}
        >
          Reports
        </button>
        <button
          className={`reports-view-tab ${activeView === 'trends' ? 'active' : ''}`}
          onClick={() => setActiveView('trends')}
        >
          Trends
        </button>
        <button
          className={`reports-view-tab ${activeView === 'compliance' ? 'active' : ''}`}
          onClick={() => setActiveView('compliance')}
        >
          Compliance
        </button>
      </div>

      {/* Trends Dashboard View */}
      {activeView === 'trends' && (
        <div className="trends-container">
          {/* Asset Selector */}
          {trendAssets.length > 1 && (
            <div className="trends-asset-selector">
              <label className="filter-label">Asset</label>
              <select
                className="filter-select"
                value={selectedAsset}
                onChange={(e) => setSelectedAsset(e.target.value)}
              >
                <option value="all">All Assets</option>
                {trendAssets.map(a => (
                  <option key={a.id} value={a.id}>{a.value}</option>
                ))}
              </select>
            </div>
          )}

          {trendLoading ? (
            <div className="loading-state">
              <div className="loading-spinner"></div>
              <p className="loading-text">LOADING TRENDS...</p>
            </div>
          ) : trendData && (trendData.riskScores?.length > 0 || trendData.vulnCounts?.length > 0 || trendData.driftActivity?.length > 0) ? (
            <div className="trends-charts">
              <RiskTrendChart data={trendData.riskScores} />
              <VulnTrendChart data={trendData.vulnCounts} />
              {trendData.vulnCounts?.length > 0 && (
                <div className="trend-chart-card">
                  <div className="trend-chart-header">
                    <h3 className="trend-chart-title">Severity Heatmap</h3>
                    <span className="trend-chart-period">Last {trendData.vulnCounts.length} weeks</span>
                  </div>
                  <div className="trend-chart-body">
                    <SeverityHeatcalendar
                      data={trendData.vulnCounts}
                      width={760}
                      height={220}
                      title="Severity Heatmap by Week"
                      desc={`Per-week count of findings by severity (critical, high, medium, low) across ${trendData.vulnCounts.length} weeks. Darker cells indicate higher counts within that severity row.`}
                      periodLabel="week"
                    />
                  </div>
                </div>
              )}
              <DriftActivityChart data={trendData.driftActivity} />
            </div>
          ) : (
            <div className="no-scans-state">
              <div className="no-scans-icon">&#x1F4C8;</div>
              <h3 className="no-scans-title">No Trend Data Yet</h3>
              <p className="no-scans-text">
                Reports are generated weekly for Pro+ tiers. Run scans and check back after your first weekly report cycle.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Compliance Dashboard View */}
      {activeView === 'compliance' && (
        <ComplianceDashboard
          findings={reports
            .filter(r => r.status === 'complete' && r.reportType === 'scan')
            .flatMap(r => r.findings || [])
          }
        />
      )}

      {activeView === 'reports' && (
      <>
      {/* end compliance gate */}

      {/* Filter Bar */}
      <div className="reports-filter-bar">
        <div className="filter-group">
          <label className="filter-label">Type</label>
          <select
            className="filter-select"
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
          >
            {TYPE_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Risk Level</label>
          <select
            className="filter-select"
            value={filterRisk}
            onChange={(e) => setFilterRisk(e.target.value)}
          >
            {RISK_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label className="filter-label">Status</label>
          <select
            className="filter-select"
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="filter-group filter-group-target">
          <label className="filter-label">Target</label>
          <input
            type="text"
            className="filter-input"
            placeholder="Search targets..."
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value)}
          />
        </div>

        {hasActiveFilters && (
          <button className="filter-clear-btn" onClick={handleClearFilters}>
            Clear Filters
          </button>
        )}
      </div>

      {/* No Reports Message */}
      {reports.length === 0 ? (
        <div className="no-scans-state">
          <div className="no-scans-icon">📋</div>
          <h3 className="no-scans-title">No Reports Found</h3>
          <p className="no-scans-text">
            Run your first security assessment to see results here.
          </p>
          <button
            className="nav-button primary"
            onClick={() => navigate('/comprehensive-scan')}
          >
            Initiate Deep Scan
          </button>
        </div>
      ) : (
        /* Reports Table */
        <div className="reports-table-wrapper">
          <table className="reports-table">
            <thead>
              <tr>
                <th className={getSortClass('createdAt')} onClick={() => handleSort('createdAt')}>
                  Date & Time
                </th>
                <th className={getSortClass('type')} onClick={() => handleSort('type')}>
                  Type
                </th>
                <th className={getSortClass('target')} onClick={() => handleSort('target')}>
                  Target
                </th>
                <th className={getSortClass('riskLevel')} onClick={() => handleSort('riskLevel')}>
                  Risk Level
                </th>
                <th className={getSortClass('status')} onClick={() => handleSort('status')}>
                  Status
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSortedReports.map((report) => (
                <tr key={report.id} onClick={() => report.status === 'complete' && handleViewReport(report)}>
                  {/* Date & Time */}
                  <td className="cell-date">
                    <span className="date-value">
                      {new Date(report.createdAt).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </span>
                    <span className="time-value">
                      {new Date(report.createdAt).toLocaleTimeString('en-US', {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                  </td>

                  {/* Type Badge */}
                  <td className="cell-type">
                    <span
                      className="type-badge"
                      style={getReportTypeBadgeStyle(report)}
                    >
                      {getReportTypeLabel(report)}
                    </span>
                  </td>

                  {/* Target */}
                  <td className="cell-target">
                    <span className="target-value">{report.target}</span>
                  </td>

                  {/* Risk Level */}
                  <td className="cell-risk">
                    <span
                      className="risk-badge"
                      style={{
                        backgroundColor: `${getRiskLevelColor(report.riskLevel)}20`,
                        color: getRiskLevelColor(report.riskLevel),
                        border: `1px solid ${getRiskLevelColor(report.riskLevel)}`
                      }}
                    >
                      {report.riskLevel || 'Unknown'}
                    </span>
                  </td>

                  {/* Status */}
                  <td className="cell-status">
                    <span
                      className="status-badge"
                      style={getStatusStyle(report.status)}
                    >
                      {report.status ? report.status.toUpperCase() : 'UNKNOWN'}
                    </span>
                  </td>

                  {/* Actions */}
                  <td className="cell-actions">
                    <div className="action-buttons">
                      <button
                        className={`action-button ${report.status === 'complete' ? 'active' : 'disabled'}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (report.status === 'complete') handleViewReport(report);
                        }}
                        disabled={report.status !== 'complete'}
                        title="View Report"
                      >
                        {report.status === 'complete' ? 'VIEW' : 'PENDING'}
                      </button>
                      {/* Only show PDF button for scans */}
                      {report.reportType === 'scan' && (
                        <button
                          className={`action-button pdf-button ${report.status === 'complete' ? 'active' : 'disabled'}`}
                          onClick={(e) => handleDownloadPDF(e, report)}
                          disabled={report.status !== 'complete' || pdfLoading[report.id]}
                          title="Download PDF Report"
                        >
                          {pdfLoading[report.id] ? (
                            <span className="pdf-loading">...</span>
                          ) : (
                            'PDF'
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Table Footer */}
          <div className="table-footer">
            <span className="footer-text">
              Showing {filteredAndSortedReports.length} of {reports.length} report{reports.length !== 1 ? 's' : ''}
              {' • '}
              Sorted by {SORTABLE_COLUMNS.find(c => c.key === sortColumn)?.label || sortColumn} ({sortDirection === 'desc' ? 'newest first' : 'oldest first'})
            </span>
          </div>
        </div>
      )}

      </>
      )}

      {/* Action Bar */}
      <div className="reports-actions">
        <button
          className="nav-button"
          onClick={() => navigate('/fast-scan')}
        >
          Fast Scan
        </button>
        <button
          className="nav-button"
          onClick={() => navigate('/validate')}
        >
          VALIDATE
        </button>
        <button
          className="nav-button primary"
          onClick={() => navigate('/comprehensive-scan')}
        >
          New Deep Scan
        </button>
      </div>
    </div>
    </CleanModernLayout>
  );
}

export default Reports;
